import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { discount_rule_list, discount_rule_save } from "../../../apis/Course";

// Discount rules.
//
// Only one discount ever applies to a tuition total, and it is always taken
// off tuition before material and textbook fees are added — those are never
// discounted.
//
// The conditions here decide when a rule applies *by itself*. "Allow staff to
// apply manually" matters because the printed brochure ties Early Bird to a
// payment cut-off, while in practice staff also grant it to families who pay
// late in person. Rather than pretend the printed rule is what happens, the
// rule can be applied by hand at enrolment.

const BRAND = "#A6192E";

const s = {
  page: { padding: "0 0 40px" },
  intro: { fontSize: 13, color: "#6b7280", margin: "0 0 18px", lineHeight: 1.7, maxWidth: 780 },
  panel: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    padding: "18px 18px 20px",
    marginBottom: 18,
  },
  panelTitle: { fontSize: 14, fontWeight: 700, color: "#1f2937", margin: "0 0 14px" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: {
    textAlign: "left",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.4px",
    textTransform: "uppercase",
    color: "#6b7280",
    borderBottom: "1px solid #e5e7eb",
    padding: "8px 10px",
  },
  td: { padding: "10px", borderBottom: "1px solid #f3f4f6", color: "#374151" },
  pct: { fontWeight: 700, color: "#111827" },
  pill: {
    display: "inline-block",
    fontSize: 10,
    fontWeight: 700,
    borderRadius: 999,
    padding: "2px 9px",
  },
  on: { background: "#dcfce7", color: "#166534" },
  off: { background: "#f3f4f6", color: "#6b7280" },
  auto: { background: "#e0e7ff", color: "#3730a3" },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    gap: 12,
  },
  field: { display: "flex", flexDirection: "column", gap: 4 },
  label: { fontSize: 12, fontWeight: 600, color: "#4b5563" },
  hint: { fontSize: 11, color: "#9ca3af" },
  input: {
    height: 38,
    borderRadius: 6,
    border: "1px solid #d1d5db",
    padding: "0 10px",
    fontSize: 14,
    width: "100%",
  },
  btn: {
    background: BRAND,
    color: "#fff",
    border: "none",
    borderRadius: 6,
    padding: "9px 20px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  btnGhost: {
    background: "#fff",
    color: "#374151",
    border: "1px solid #d1d5db",
    borderRadius: 6,
    padding: "8px 16px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  btnLink: { background: "none", border: "none", color: BRAND, fontSize: 12, cursor: "pointer", padding: 0 },
  note: {
    background: "#fffbeb",
    border: "1px solid #fde68a",
    borderRadius: 8,
    padding: "12px 14px",
    fontSize: 12.5,
    lineHeight: 1.7,
    color: "#78350f",
    marginTop: 14,
  },
};

const blank = {
  id: null,
  name: "",
  code: "",
  percentage: "",
  min_weeks: "",
  cutoff_date: "",
  applies_from_nth: "",
  allow_manual: true,
  status: 1,
};

const DiscountRules = () => {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const res = await discount_rule_list();
    setRows(Array.isArray(res?.data) ? res.data : []);
  };

  useEffect(() => {
    load();
  }, []);

  const set = (k) => (e) =>
    setForm((f) => ({
      ...f,
      [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    }));

  const save = async () => {
    if (!form.name.trim() || !form.code.trim()) {
      toast.error("Name and code are both required");
      return;
    }
    if (form.percentage === "" || Number(form.percentage) <= 0) {
      toast.error("Enter a percentage above zero");
      return;
    }
    setSaving(true);
    const res = await discount_rule_save({
      ...form,
      min_weeks: form.min_weeks === "" ? null : Number(form.min_weeks),
      applies_from_nth:
        form.applies_from_nth === "" ? null : Number(form.applies_from_nth),
      cutoff_date: form.cutoff_date || null,
    });
    setSaving(false);
    if (res?.status) {
      setForm(blank);
      load();
    }
  };

  const edit = (r) =>
    setForm({
      id: r.id,
      name: r.name,
      code: r.code,
      percentage: r.percentage,
      min_weeks: r.min_weeks ?? "",
      cutoff_date: r.cutoff_date ? String(r.cutoff_date).slice(0, 10) : "",
      applies_from_nth: r.applies_from_nth ?? "",
      allow_manual: r.allow_manual === 1 || r.allow_manual === true,
      status: r.status,
    });

  const toggleStatus = async (r) => {
    const res = await discount_rule_save({
      ...r,
      cutoff_date: r.cutoff_date ? String(r.cutoff_date).slice(0, 10) : null,
      status: r.status === 1 ? 0 : 1,
    });
    if (res?.status) load();
  };

  const conditionText = (r) => {
    const bits = [];
    if (r.applies_from_nth) bits.push(`from subject ${r.applies_from_nth}`);
    if (r.code === "sibling") bits.push("sibling enrolled");
    if (r.min_weeks) bits.push(`${r.min_weeks}+ weeks`);
    if (r.cutoff_date) bits.push(`paid by ${String(r.cutoff_date).slice(0, 10)}`);
    return bits.length ? bits.join(" · ") : "staff applied only";
  };

  return (
    <div className="right-nav" id="dash-event" style={s.page}>
      <div className="modal-content">
        <div className="modal-header">
          <div className="col-md-6">
            <h4 className="modal-title">Discount Rules</h4>
          </div>
        </div>

        <div className="modal-body">
          <p style={s.intro}>
            Only one discount is ever applied, and always to tuition before
            material and textbook fees are added &mdash; those are never
            discounted. When more than one rule matches, the largest percentage
            wins.
          </p>

          <div style={s.panel}>
            <p style={s.panelTitle}>Current rules</p>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Name</th>
                  <th style={s.th}>Code</th>
                  <th style={s.th}>Off tuition</th>
                  <th style={s.th}>Applies automatically when</th>
                  <th style={s.th}>Staff can apply</th>
                  <th style={s.th}>Status</th>
                  <th style={s.th}></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td style={s.td}>{r.name}</td>
                    <td style={s.td}>
                      <code>{r.code}</code>
                    </td>
                    <td style={{ ...s.td, ...s.pct }}>{Number(r.percentage)}%</td>
                    <td style={s.td}>{conditionText(r)}</td>
                    <td style={s.td}>
                      <span
                        style={{
                          ...s.pill,
                          ...(r.allow_manual ? s.on : s.auto),
                        }}
                      >
                        {r.allow_manual ? "YES" : "AUTO ONLY"}
                      </span>
                    </td>
                    <td style={s.td}>
                      <span style={{ ...s.pill, ...(r.status === 1 ? s.on : s.off) }}>
                        {r.status === 1 ? "ACTIVE" : "OFF"}
                      </span>
                    </td>
                    <td style={s.td}>
                      <button type="button" style={s.btnLink} onClick={() => edit(r)}>
                        Edit
                      </button>
                      <button
                        type="button"
                        style={{ ...s.btnLink, color: "#6b7280", marginLeft: 12 }}
                        onClick={() => toggleStatus(r)}
                      >
                        {r.status === 1 ? "Turn off" : "Turn on"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={s.panel}>
            <p style={s.panelTitle}>
              {form.id ? `Edit “${form.name}”` : "Add a rule"}
            </p>
            <div style={s.grid}>
              <div style={s.field}>
                <label style={s.label}>Name *</label>
                <input style={s.input} value={form.name} onChange={set("name")} placeholder="Early Bird" />
              </div>
              <div style={s.field}>
                <label style={s.label}>Code *</label>
                <input style={s.input} value={form.code} onChange={set("code")} placeholder="early_bird" />
                <span style={s.hint}>Used by the calculator</span>
              </div>
              <div style={s.field}>
                <label style={s.label}>Percentage off *</label>
                <input
                  type="number"
                  step="0.5"
                  style={s.input}
                  value={form.percentage}
                  onChange={set("percentage")}
                  placeholder="10"
                />
              </div>
              <div style={s.field}>
                <label style={s.label}>Minimum weeks</label>
                <input
                  type="number"
                  style={s.input}
                  value={form.min_weeks}
                  onChange={set("min_weeks")}
                  placeholder="8"
                />
                <span style={s.hint}>Blank = no limit</span>
              </div>
              <div style={s.field}>
                <label style={s.label}>Pay by</label>
                <input
                  type="date"
                  style={s.input}
                  value={form.cutoff_date}
                  onChange={set("cutoff_date")}
                />
                <span style={s.hint}>Early bird cut-off</span>
              </div>
              <div style={s.field}>
                <label style={s.label}>From subject number</label>
                <input
                  type="number"
                  style={s.input}
                  value={form.applies_from_nth}
                  onChange={set("applies_from_nth")}
                  placeholder="3"
                />
                <span style={s.hint}>3 = third subject onward</span>
              </div>
              <div style={s.field}>
                <label style={s.label}>Options</label>
                <div style={{ display: "flex", gap: 16, alignItems: "center", height: 38 }}>
                  <label style={{ fontSize: 12, color: "#4b5563" }}>
                    <input
                      type="checkbox"
                      checked={form.allow_manual}
                      onChange={set("allow_manual")}
                    />{" "}
                    Staff can apply
                  </label>
                </div>
              </div>
            </div>

            <div style={{ marginTop: 14, display: "flex", gap: 10 }}>
              <button type="button" style={s.btn} disabled={saving} onClick={save}>
                {saving ? "Saving…" : form.id ? "Save changes" : "Add rule"}
              </button>
              <button type="button" style={s.btnGhost} onClick={() => setForm(blank)}>
                Clear
              </button>
            </div>

            <div style={s.note}>
              A rule only applies on its own when the fact it depends on is
              known &mdash; a sibling being enrolled, the subject count, or a
              payment date inside the cut-off. Enrolling for enough weeks is a
              limit, not a reason, so it never triggers a discount by itself.
              Leave <strong>Staff can apply</strong> ticked for anything the
              office grants case by case.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DiscountRules;
